import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
  BASIC_INFO_FORM_IDS,
} from 'providers/FreeTrialProvider';
import { hasValue } from 'swipeclock-frontend';
import { useFreeTrialInputHandler } from 'hooks/useFreeTrialInputHandler';
import { validatePassword } from 'helpers/validatePassword';
import {
  LogoImage,
  BasicInfoFormInputGrid,
  FormInputHeader,
  FormInputSubHeader,
  FormInputFooter,
  FormDivider,
  StateSelect,
  TimezoneSelect,
  PasswordField,
  ConfirmPasswordField,
  PageSubtitle,
} from 'features/freeTrial';
import {
  usePostCreateMutation,
  SHARED_POST_CREATE_CACHE_KEY,
} from 'services/freeTrial';
import { masterDataApi } from 'services/masterDataApi';
import { useLoaderData } from 'react-router-dom';
import Button from 'components/Button';
import EastIcon from '@mui/icons-material/East';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export async function loader(store) {
  const stateRequest = store.dispatch(
    masterDataApi.endpoints.getStateOptions.initiate()
  );
  const stateResponse = await stateRequest.unwrap();
  const stateOptionsList = formatForDropdown(stateResponse);
  const timezoneRequest = store.dispatch(
    masterDataApi.endpoints.getTimezoneOptions.initiate()
  );
  const timezoneResponse = await timezoneRequest.unwrap();
  const timezoneOptionsList = formatForDropdown(timezoneResponse);

  return {
    stateOptionsList,
    timezoneOptionsList,
  };
}

export default function BasicInfoForm() {
  const theme = useTheme();
  const navigateToPage = useFreeTrialPageNavigation();
  const { stateOptionsList, timezoneOptionsList } = useLoaderData();
  const { state, actions, meta } = useFreeTrialContext();
  const page = meta.pages.BASIC_INFO_FORM;
  const defaultValues = state?.formBag[page] || {};
  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    handleSelect,
    isValid,
    validationRef,
  } = useFreeTrialInputHandler({
    ids: [
      BASIC_INFO_FORM_IDS.STATE,
      BASIC_INFO_FORM_IDS.TIME_ZONE,
      BASIC_INFO_FORM_IDS.PASSWORD,
      BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD,
    ],
    validationCallback,
    page,
  });

  // Add a fixed cache key to share with other components
  const [postCreate] = usePostCreateMutation({
    fixedCacheKey: SHARED_POST_CREATE_CACHE_KEY,
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    if (isValid()) {
      const values = Object.keys(validationRef.current).reduce((acc, key) => {
        // Remove the confirm password value
        if (key === BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD) {
          return acc;
        }

        // Convert the time zone value to an integer
        if (key === BASIC_INFO_FORM_IDS.TIME_ZONE) {
          acc[key] = parseInt(validationRef.current[key]?.value, 10);
          return acc;
        }

        acc[key] = validationRef.current[key]?.value;
        return acc;
      }, {});

      // Pass values to the providers formBag
      // Add back the company name so it doesn't glitch out when routing the page.
      actions.setFormValues(
        {
          ...values,
          [BASIC_INFO_FORM_IDS.COMPANY_NAME]: defaultValues?.companyName,
        },
        page
      );
      // Not using unwrap because we don't need to wait for the response
      // We are handling the response in the site creating page
      postCreate({
        body: { ...values, registrationToken: state?.registrationToken },
      });

      // Go to site creating page
      navigateToPage(meta.pages.SITE_CREATING);
    }
  };

  return (
    <BasicInfoFormInputGrid>
      <FormInputHeader>
        <Stack direction="row" alignItems="center" gap={2} sx={{ pb: 1 }}>
          <LogoImage />
          Basic Info
        </Stack>
        <span>
          We just need some basic information to complete your account setup.
        </span>
      </FormInputHeader>
      <PageSubtitle sx={{ gridArea: 'company' }}>
        Company Name:{' '}
        <span>{defaultValues?.companyName || 'Placeholder Name'}</span>
      </PageSubtitle>
      <StateSelect
        error={getError(BASIC_INFO_FORM_IDS.STATE)}
        id={BASIC_INFO_FORM_IDS.STATE}
        label="State"
        onChange={handleSelect(BASIC_INFO_FORM_IDS.STATE)}
        options={stateOptionsList}
        required
        validationRef={validationRef}
        defaultValue={defaultValues?.state || ''}
        hint="WorkforceHub is currently only available in the United States and its territories."
      />
      <TimezoneSelect
        error={getError(BASIC_INFO_FORM_IDS.TIME_ZONE)}
        id={BASIC_INFO_FORM_IDS.TIME_ZONE}
        label="Time Zone"
        onChange={handleSelect(BASIC_INFO_FORM_IDS.TIME_ZONE)}
        options={timezoneOptionsList}
        required
        validationRef={validationRef}
        defaultValue={defaultValues?.timeZone || ''}
      />
      <FormDivider />
      <FormInputSubHeader>
        Password Setup
        <span className="password-email">
          Create a password to use with your email to log into
          WorkforceHub.{' '}
        </span>
        <span>
          Your password must be a minimum of 8 characters and include at least
          one uppercase letter, one lowercase letter, one number, and one
          special character.
        </span>
      </FormInputSubHeader>
      <PasswordField
        autoComplete="password"
        error={getError(BASIC_INFO_FORM_IDS.PASSWORD)}
        id={BASIC_INFO_FORM_IDS.PASSWORD}
        label="Password"
        maxLength={50}
        onBlur={handleBlur(BASIC_INFO_FORM_IDS.PASSWORD)}
        onChange={handleChange(BASIC_INFO_FORM_IDS.PASSWORD)}
        defaultValue={defaultValues?.password || ''}
        placeholder="Enter your password"
        required
        validationRef={validationRef}
      />
      <ConfirmPasswordField
        autoComplete="password"
        error={getError(BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD)}
        id={BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD}
        label="Confirm Password"
        maxLength={50}
        onBlur={handleBlur(BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD)}
        onChange={handleChange(BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD)}
        defaultValue={defaultValues?.confirmPassword || ''}
        placeholder="Enter your password again"
        required
        validationRef={validationRef}
      />
      <FormDivider sx={{ gridArea: 'divider2' }} />
      <FormInputFooter>
        <Typography
          variant="caption"
          sx={{ '& span': { color: theme.palette.primary.main } }}
        >
          <span>*</span> Indicates a required field.
        </Typography>
        <Button
          color="secondary"
          disabled={disabled}
          endIcon={<EastIcon sx={{ mb: '2px', ml: '4px' }} fontSize="small" />}
          onClick={handleSubmit}
          sx={{ width: 'max-content', height: '48px', marginLeft: 'auto' }}
          variant="contained"
        >
          Submit
        </Button>
      </FormInputFooter>
    </BasicInfoFormInputGrid>
  );
}

// ------------------------------------------------------------- //
// Validation
// ------------------------------------------------------------- //

const validationCallback = (id, value, ref) => {
  let error = null;

  switch (id) {
    case BASIC_INFO_FORM_IDS.STATE:
      if (!hasValue(value) || value.length !== 2) {
        error = 'Valid State abbreviation is required.';
      }
      break;
    case BASIC_INFO_FORM_IDS.TIME_ZONE:
      if (!hasValue(value)) {
        error = 'Time Zone is required.';
      }
      break;
    case BASIC_INFO_FORM_IDS.PASSWORD:
      if (!hasValue(value)) {
        error = 'Password is required.';
      } else if (value.length > 50) {
        error = 'Password cannot exceed 50 characters.';
      } else {
        const { pass, error: passwordError } = validatePassword(value);
        if (!pass) {
          error = passwordError;
        }
      }
      break;
    case BASIC_INFO_FORM_IDS.CONFIRM_PASSWORD:
      if (!hasValue(value)) {
        error = 'Confirm Password is required.';
      } else if (value !== ref.current[BASIC_INFO_FORM_IDS.PASSWORD]?.value) {
        error = 'Password and Confirm Password must match.';
      }
      break;
    default:
      break;
  }

  return error;
};

/** Helper function */

// this helper function takes in data returned by the api and converts it into
// a format that can be used by dropdown components
// Not performing the transformation in the api because we may not always want
// to use this data in dropdowns.
const formatForDropdown = (data) => {
  return data.map((obj, idx) => ({
    label: obj?.name || idx,
    value: obj?.id || idx,
  }));
};
